import React, { useState, useEffect } from 'react';
import Modal from 'components/_layouts/Modal';
import {
  Box,
  Stack,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';
import { HeaderConfigurationResponse } from 'views/App/Portal/Policy/_types/manuals/draft';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import AttachmentSelectorInline from 'views/App/Portal/Policy/DraftManuals/_components/AttachmentSelectorInline';
import { Attachment } from 'views/App/Portal/Policy/_types/manuals/draft';
import { formatDateForSave, formatDateForLoad } from 'views/App/Portal/Policy/_helpers/policyHeaderDateHelper';

interface PolicyHeadersEditModalProps {
  open: boolean;
  onClose: () => void;
  manualHeaders?: HeaderConfigurationResponse;
  policyHeaders?: any[];
  manualId: string;
  policyData?: any;
  attachments?: Attachment[];
  onSave?: (data: any) => Promise<void>;
  manualLevel?: string;
}

const PolicyHeadersEditModal: React.FC<PolicyHeadersEditModalProps> = ({
  open,
  onClose,
  manualHeaders,
  policyHeaders,
  manualId,
  policyData,
  attachments = [],
  onSave,
  manualLevel,
}) => {
  const [saving, setSaving] = useState(false);

  // Date fields
  const [effectiveDate, setEffectiveDate] = useState<Dayjs | null>(null);
  const [revisedDate, setRevisedDate] = useState<Dayjs | null>(null);

  // Agency logo
  const [agencyLogoId, setAgencyLogoId] = useState<string>('');

  // Issuing authority - type comes from manual config, not editable here
  const [issuingAuthoritySignatureId, setIssuingAuthoritySignatureId] = useState<string>('');
  const [issuingAuthorityName, setIssuingAuthorityName] = useState<string>('');

  // Custom fields
  const [custom1Header, setCustom1Header] = useState<string>('');
  const [custom1Value, setCustom1Value] = useState<string>('');
  const [custom2Header, setCustom2Header] = useState<string>('');
  const [custom2Value, setCustom2Value] = useState<string>('');

  useEffect(() => {
    if (open) {
      // Reset all fields first
      setEffectiveDate(null);
      setRevisedDate(null);
      setAgencyLogoId('');
      setIssuingAuthoritySignatureId('');
      setIssuingAuthorityName('');
      setCustom1Header('');
      setCustom1Value('');
      setCustom2Header('');
      setCustom2Value('');

      // Check if dates are set to autofill
      const isEffectiveDateAutofill = manualHeaders?.autoSetEffectiveDate === true;
      const isRevisedDateAutofill = manualHeaders?.autoSetRevisedDate === true;

      // If dates are set to autofill, set them to current date
      if (isEffectiveDateAutofill) {
        setEffectiveDate(dayjs());
      }
      if (isRevisedDateAutofill) {
        setRevisedDate(dayjs());
      }

      // Initialize from manualHeaders (defaults)
      if (manualHeaders && manualHeaders.state === 'ENABLED') {
        setAgencyLogoId(manualHeaders.agencyLogo || '');
        setIssuingAuthoritySignatureId(manualHeaders.issuingAuthoritySignature || '');
        setIssuingAuthorityName(manualHeaders.issuingAuthorityName || '');

        if (manualHeaders.customFields) {
          const custom1 = manualHeaders.customFields[0];
          const custom2 = manualHeaders.customFields[1];
          setCustom1Header(custom1?.header || '');
          setCustom1Value(custom1?.value || '');
          setCustom2Header(custom2?.header || '');
          setCustom2Value(custom2?.value || '');
        }
      }

      // Override with policyHeaders if available (policy-specific values take precedence)
      // BUT: Skip date fields if they are set to autofill - always use current date
      if (policyHeaders && policyHeaders.length > 0) {
        policyHeaders.forEach((header) => {
          switch (header.type) {
            case 'EFFECTIVE_DATE':
              // Only load saved value if NOT autofill
              if (header.value && !isEffectiveDateAutofill) {
                const effectiveDateFormat = header.dateFormat || manualHeaders?.effectiveDateFormat;
                setEffectiveDate(formatDateForLoad(header.value, effectiveDateFormat));
              }
              break;
            case 'REVISED_DATE':
              // Only load saved value if NOT autofill
              if (header.value && !isRevisedDateAutofill) {
                const revisedDateFormat = header.dateFormat || manualHeaders?.revisedDateFormat;
                setRevisedDate(formatDateForLoad(header.value, revisedDateFormat));
              }
              break;
            case 'AGENCY_LOGO':
              setAgencyLogoId(header.agencyLogo || '');
              break;
            case 'ISSUING_AUTHORITY':
              setIssuingAuthoritySignatureId(header.issuingAuthoritySignature || '');
              setIssuingAuthorityName(header.issuingAuthorityName || '');
              break;
            case 'CUSTOM1':
              setCustom1Header(header.header || '');
              setCustom1Value(header.value || '');
              break;
            case 'CUSTOM2':
              setCustom2Header(header.header || '');
              setCustom2Value(header.value || '');
              break;
          }
        });
      }
    }
  }, [open, manualHeaders, policyHeaders]);

  const handleSave = async () => {
    setSaving(true);
    try {

      if (onSave) {
        const data = {
          effectiveDate: formatDateForSave(effectiveDate, manualHeaders?.effectiveDateFormat),
          revisedDate: formatDateForSave(revisedDate, manualHeaders?.revisedDateFormat),
          agencyLogo: agencyLogoId,
          issuingAuthoritySignature: issuingAuthoritySignatureId,
          issuingAuthorityName,
          customFields: [
            { header: custom1Header, value: custom1Value },
            { header: custom2Header, value: custom2Value },
          ],
        };
        await onSave(data);
        enqueueSnackbar(t('policy.stagedManuals.policyHeaders.saveSuccess'), { variant: 'success' });
      }
      onClose();
    } catch (e) {
      console.error(e);
      let errorMessage = t('policy.stagedManuals.policyHeaders.saveError');
      if (e && typeof e === 'object') {
        const errorObj = e as any;
        if (errorObj.httpStatus === 409) {
          errorMessage = t('policy.stagedManuals.policyHeaders.saveErrorLocked');
        }
      }
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset all state to initial values
    setSaving(false);
    onClose();
  };

  // Agency manuals can edit policy headers, but GLOBAL and JURISDICTION cannot
  const isReadOnly = manualLevel !== 'AGENCY';

  // Check if dates are set to autofill
  const isEffectiveDateAutofill = manualHeaders?.autoSetEffectiveDate === true;
  const isRevisedDateAutofill = manualHeaders?.autoSetRevisedDate === true;

  // Get issuing authority type from manual configuration (not editable in modal)
  const issuingAuthorityType = manualHeaders?.issuingAuthorityType || 'SIGNATURE_AND_NAME';
  const showSignature = issuingAuthorityType === 'SIGNATURE_AND_NAME' || issuingAuthorityType === 'SIGNATURE_ONLY';
  const showName = issuingAuthorityType === 'SIGNATURE_AND_NAME' || issuingAuthorityType === 'NAME_ONLY';

  // Determine which headers are enabled based on manual configuration
  const isHeaderEnabled = (headerType: string): boolean => {
    if (!manualHeaders || manualHeaders.state !== 'ENABLED' || !manualHeaders.order) {
      return false;
    }
    const orderItem = manualHeaders.order.find(item => item.type === headerType);
    return orderItem?.state === 'ENABLED';
  };

  const showEffectiveDate = isHeaderEnabled('EFFECTIVE_DATE');
  const showRevisedDate = isHeaderEnabled('REVISED_DATE');
  const showAgencyLogo = isHeaderEnabled('AGENCY_LOGO');
  const showIssuingAuthority = isHeaderEnabled('ISSUING_AUTHORITY');
  const showCustom1 = isHeaderEnabled('CUSTOM1');
  const showCustom2 = isHeaderEnabled('CUSTOM2');

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      title={t('policy.stagedManuals.policyHeaders.title')}
      subtitle={null}
      size='md'
      actions={
        isReadOnly
          ? []
          : [
            <Button
              key='cancel'
              variant='outlined'
              color='neutral'
              onClick={handleCancel}
              disabled={saving}
              aria-label={t('policy.stagedManuals.policyHeaders.cancelAria')}
            >
              {t('common.cancel')}
            </Button>,
            <Button
              key='save'
              variant='contained'
              color='primary'
              onClick={handleSave}
              disabled={saving}
              endIcon={
                saving ? (
                  <CircularProgress
                    size={20}
                    color='inherit'
                  />
                ) : null
              }
              aria-label={t('policy.stagedManuals.policyHeaders.saveAria')}
            >
              {t('common.save')}
            </Button>,
          ]
      }
    >
      <Stack spacing={4} sx={{ mt: 2 }}>
        {/* Dates - Side by Side */}
        {(showEffectiveDate || showRevisedDate) && (
          <Stack direction="row" spacing={3}>
            {showEffectiveDate && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  {t('policy.stagedManuals.policyHeaders.effectiveDate')}
                  {isEffectiveDateAutofill && ' (autofill)'}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    sx={{ paddingTop: 0 }}
                    value={effectiveDate}
                    onChange={setEffectiveDate}
                    format='MM/DD/YYYY'
                    disabled={isReadOnly || isEffectiveDateAutofill}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            )}

            {showRevisedDate && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  {t('policy.stagedManuals.policyHeaders.revisedDate')}
                  {isRevisedDateAutofill && ' (autofill)'}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    sx={{ paddingTop: 0 }}
                    value={revisedDate}
                    onChange={setRevisedDate}
                    format='MM/DD/YYYY'
                    disabled={isReadOnly || isRevisedDateAutofill}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            )}
          </Stack>
        )}

        {/* Agency Logo and Issuing Authority - Side by Side on md+, stacked on small */}
        {(showAgencyLogo || showIssuingAuthority) && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            {/* Agency Logo */}
            {showAgencyLogo && (
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  {t('policy.stagedManuals.policyHeaders.agencyLogo')}
                </Typography>
                <Box>
                  <AttachmentSelectorInline
                    manualId={manualId}
                    attachmentId={agencyLogoId}
                    onSelectAttachment={setAgencyLogoId}
                    label="Agency Logo"
                    acceptImageOnly={true}
                    attachments={attachments}
                    requireLock={true}
                  />
                </Box>
              </Box>
            )}

            {/* Issuing Authority */}
            {showIssuingAuthority && (
              <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  {t('policy.stagedManuals.policyHeaders.issuingAuthority')}
                </Typography>

                <Stack spacing={2}>
                  {/* Signature - shown if configured */}
                  {showSignature && (
                    <Box>

                      <AttachmentSelectorInline
                        manualId={manualId}
                        attachmentId={issuingAuthoritySignatureId}
                        onSelectAttachment={setIssuingAuthoritySignatureId}
                        label="Signature"
                        acceptImageOnly={true}
                        attachments={attachments}
                        requireLock={true}
                      />
                    </Box>
                  )}

                  {/* Name field - shown if configured */}
                  {showName && (
                    <Box>
                      <TextField
                        sx={{ paddingTop: 0 }}
                        fullWidth
                        size="small"

                        value={issuingAuthorityName}
                        onChange={(e) => setIssuingAuthorityName(e.target.value)}
                        disabled={isReadOnly}
                      />
                    </Box>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
        {/* Custom Fields - Side by Side */}
        {(showCustom1 || showCustom2) && (
          <Stack direction="row" spacing={3}>
            {/* Custom Field 1 */}
            {showCustom1 && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0 }}>
                  {t('policy.stagedManuals.policyHeaders.customField1')}
                </Typography>
                <Stack direction="row" spacing={2}>
                  <TextField
                    sx={{ flex: 1 }}
                    size="small"
                    label="Header"
                    value={custom1Header}
                    onChange={(e) => setCustom1Header(e.target.value)}
                    disabled={isReadOnly}
                  />
                  <TextField
                    sx={{ flex: 1 }}
                    size="small"
                    label="Value"
                    value={custom1Value}
                    onChange={(e) => setCustom1Value(e.target.value)}
                    disabled={isReadOnly}
                  />
                </Stack>
              </Box>
            )}

            {/* Custom Field 2 */}
            {showCustom2 && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0 }}>
                  {t('policy.stagedManuals.policyHeaders.customField2')}
                </Typography>
                <Stack direction="row" spacing={2}>
                  <TextField
                    sx={{ flex: 1 }}
                    size="small"
                    label="Header"
                    value={custom2Header}
                    onChange={(e) => setCustom2Header(e.target.value)}
                    disabled={isReadOnly}
                  />
                  <TextField
                    sx={{ flex: 1 }}
                    size="small"
                    label="Value"
                    value={custom2Value}
                    onChange={(e) => setCustom2Value(e.target.value)}
                    disabled={isReadOnly}
                  />
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};

export default PolicyHeadersEditModal;
